import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Ship, MapPin, Loader2, Paperclip, Anchor } from "lucide-react";
import { CruiseReservation } from "@/hooks/useTrips";
import { EditCruiseDialog } from "../dialogs/EditCruiseDialog";
import { DeleteItemDialog } from "../dialogs/DeleteItemDialog";
import { AttachmentsSheet, AttachmentsBadge, SourceEmailBadge } from "../attachments";
import { getCompanyLogoUrl } from "@/lib/companyLogos";
import { useEnrichCruise } from "@/hooks/useCruiseEnrichment";
import { safeFormat } from "@/lib/formatDateTime";

interface CruiseCardProps {
  cruise: CruiseReservation;
  tripId?: string;
  index?: number;
  readOnly?: boolean;
}

export const CruiseCard = ({ cruise, tripId, index, readOnly = false }: CruiseCardProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const enrichCruise = useEnrichCruise();

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    const result = safeFormat(dateStr, "MMM d, yyyy");
    return result === "—" ? dateStr : result;
  };

  const handleLoadItinerary = () => {
    enrichCruise.mutate({ tripId, cruiseIndex: index, cruise });
  };

  // Get cruise line logo URL
  const logoUrl = getCompanyLogoUrl(cruise.cruise_line, 64);

  // Get departure/arrival times - prefer main fields, fall back to itinerary stops
  const getDepartureTime = () => {
    if (cruise.departure_time) return cruise.departure_time;
    // Find embarkation stop or first stop with departure time
    const embarkationStop = cruise.itinerary_stops?.find(s => s.is_embarkation);
    if (embarkationStop?.departure_time) return embarkationStop.departure_time;
    // Fall back to first stop's departure time
    const firstStop = cruise.itinerary_stops?.[0];
    return firstStop?.departure_time || null;
  };

  const getArrivalTime = () => {
    if (cruise.arrival_time) return cruise.arrival_time;
    // Find disembarkation stop or last stop with arrival time
    const disembarkationStop = cruise.itinerary_stops?.find(s => s.is_disembarkation);
    if (disembarkationStop?.arrival_time) return disembarkationStop.arrival_time;
    // Fall back to last stop's arrival time
    const lastStop = cruise.itinerary_stops?.[cruise.itinerary_stops.length - 1];
    return lastStop?.arrival_time || null;
  };

  const departureTime = getDepartureTime();
  const arrivalTime = getArrivalTime();

  // Check if there are expandable details to show
  const hasExpandableDetails = !!(
    cruise.dining_time ||
    (cruise.itinerary_stops && cruise.itinerary_stops.length > 0) ||
    (cruise.amenities && cruise.amenities.length > 0)
  );

  return (
    <>
      <Card variant="interactive">
        <CardContent className="p-6">
          {/* Header with cruise line info and actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <div className="w-12 h-12 rounded bg-orange-50 border border-orange-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={logoUrl}
                    alt={cruise.cruise_line}
                    className="w-8 h-8 object-contain mix-blend-multiply"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML = `<span class="text-xs font-bold text-orange-600">${cruise.cruise_line?.substring(0, 2).toUpperCase() || 'CR'}</span>`;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded bg-orange-50 border border-orange-200 flex items-center justify-center">
                  <span className="text-xs font-bold text-orange-600">{cruise.cruise_line?.substring(0, 2).toUpperCase() || 'CR'}</span>
                </div>
              )}
              <div>
                <h3 className="font-semibold text-lg mb-1">{cruise.cruise_line}</h3>
                {cruise.ship_name && (
                  <p className="text-sm text-muted-foreground">{cruise.ship_name}</p>
                )}
                {cruise.cruise_title && (
                  <p className="text-sm text-muted-foreground">{cruise.cruise_title}</p>
                )}
              </div>
            </div>
            {!readOnly && tripId !== undefined && index !== undefined && (
              <div className="flex items-center gap-2">
                <AttachmentsBadge
                  tripId={tripId}
                  itemType="cruises"
                  itemIndex={index}
                  onClick={() => setShowAttachments(true)}
                />
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {cruise.source_email_id && (
                    <SourceEmailBadge onClick={() => setShowAttachments(true)} />
                  )}
                  <Button variant="ghost" size="icon" onClick={() => setShowAttachments(true)} title="Attachments">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowEdit(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowDelete(true)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Departure/Arrival - 2 Column Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Departure</div>
              <div className="font-semibold">{formatDate(cruise.departure_date)}</div>
              <div className="text-sm text-muted-foreground">{departureTime || 'N/A'}</div>
              {cruise.departure_port && (
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Anchor className="w-3 h-3" />
                  {cruise.departure_port}
                </div>
              )}
            </div>
            {cruise.arrival_date && (
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">Arrival</div>
                <div className="font-semibold">{formatDate(cruise.arrival_date)}</div>
                <div className="text-sm text-muted-foreground">{arrivalTime || 'N/A'}</div>
                {cruise.arrival_port && (
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Anchor className="w-3 h-3" />
                    {cruise.arrival_port}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
            {cruise.cabin_type && (
              <div>
                <span className="font-medium">Cabin:</span> {cruise.cabin_type}
              </div>
            )}
            {cruise.cabin_number && (
              <div>
                <span className="font-medium">#</span> {cruise.cabin_number}
              </div>
            )}
            {cruise.deck && (
              <div>
                <span className="font-medium">Deck:</span> {cruise.deck}
              </div>
            )}
            {cruise.confirmation_number && (
              <div>
                <span className="font-medium">Conf:</span> {cruise.confirmation_number}
              </div>
            )}
          </div>

          {/* Additional Confirmation Numbers */}
          {cruise.additional_confirmation_numbers && cruise.additional_confirmation_numbers.length > 0 && (
            <div className="text-sm text-muted-foreground mb-4">
              <span className="font-medium">Additional Confirmations:</span>{" "}
              {cruise.additional_confirmation_numbers.join(", ")}
            </div>
          )}

          {/* Passengers */}
          {cruise.passengers && cruise.passengers.length > 0 && (
            <div className="text-sm text-muted-foreground mb-4">
              <span className="font-medium">Passengers:</span>{" "}
              {cruise.passengers.map(p => p.name).join(", ")}
            </div>
          )}

          {/* Load Itinerary Button */}
          {!readOnly && !cruise.itinerary_stops?.length && !cruise.enriched && (
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadItinerary}
                disabled={enrichCruise.isPending}
              >
                {enrichCruise.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <MapPin className="h-3.5 w-3.5 mr-1.5" />
                    Load Itinerary
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Expandable Details */}
          {expanded && (
            <div className="border-t border-border pt-4 space-y-3">
              {cruise.dining_time && (
                <div>
                  <span className="text-sm font-medium">Dining Time:</span>
                  <span className="text-sm text-muted-foreground ml-2">{cruise.dining_time}</span>
                </div>
              )}

              {/* Itinerary Stops */}
              {cruise.itinerary_stops && cruise.itinerary_stops.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Itinerary:</span>
                  <div className="mt-2 space-y-2">
                    {cruise.itinerary_stops.map((stop, idx) => (
                      <div key={idx} className="text-sm text-muted-foreground ml-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-medium">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <span className="font-medium text-foreground">{stop.port_name}</span>
                          {stop.country && <span>, {stop.country}</span>}
                          {stop.arrival_date && (
                            <span className="ml-2">
                              {formatDate(stop.arrival_date)}
                              {stop.arrival_time && ` ${stop.arrival_time}`}
                              {stop.departure_time && ` - ${stop.departure_time}`}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cruise.amenities && cruise.amenities.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Amenities:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {cruise.amenities.map((amenity, idx) => (
                      <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Toggle Button - only show if there are details to expand */}
          {hasExpandableDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-4 text-sm text-accent hover:text-accent/80 transition-colors"
            >
              {expanded ? 'Show less' : 'Show more details'}
            </button>
          )}
        </CardContent>
      </Card>

      {!readOnly && tripId !== undefined && index !== undefined && (
        <>
          <EditCruiseDialog
            cruise={cruise}
            tripId={tripId}
            index={index}
            open={showEdit}
            onOpenChange={setShowEdit}
          />
          <DeleteItemDialog
            tripId={tripId}
            itemType="cruises"
            index={index}
            itemName={`${cruise.cruise_line} - ${cruise.ship_name}`}
            open={showDelete}
            onOpenChange={setShowDelete}
          />
          <AttachmentsSheet
            open={showAttachments}
            onOpenChange={setShowAttachments}
            tripId={tripId}
            itemType="cruises"
            itemIndex={index}
            itemTitle={`${cruise.cruise_line} - ${cruise.ship_name}`}
            sourceEmailId={cruise.source_email_id}
          />
        </>
      )}
    </>
  );
};
